import React, { useState, useEffect } from 'react';
import NavigationSidebar from '../../components/navigation/NavigationSidebar';
import UserProfileDropdown from '../../components/navigation/UserProfileDropdown';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import ProfileHeader from './components/ProfileHeader';
import PersonalInfoTab from './components/PersonalInfoTab';
import DocumentsTab from './components/DocumentsTab';
import TravelInfoTab from './components/TravelInfoTab';
import FinancialTab from './components/FinancialTab';
import CaseHistoryTab from './components/CaseHistoryTab';
import Icon from '../../components/AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';

const PilgrimProfile = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { currentLanguage, t } = useLanguage();
  const [activeTab, setActiveTab] = useState('personal');

  const mockUser = {
    name: "Ahmed Benali",
    role: "travel_agent",
    agency: "Omra Voyages Maroc",
    avatar: "https://randomuser.me/api/portraits/men/45.jpg"
  };

  const mockNotifications = [
    {
      id: 1,
      title: t('pilgrimProfile.documentsApproved'),
      message: t('pilgrimProfile.passportApprovedNotification'),
      timestamp: new Date(Date.now() - 1800000),
      priority: 'medium',
      read: false
    },
    {
      id: 2,
      title: t('pilgrimProfile.paymentReceived'),
      message: t('pilgrimProfile.paymentReceivedNotification'),
      timestamp: new Date(Date.now() - 3600000),
      priority: 'high',
      read: false
    }
  ];

  const mockPilgrim = {
    id: "PIL-2026-001",
    caseNumber: "OM2026-1234",
    firstName: "Fatima",
    lastName: "Hassan",
    arabicFirstName: "فاطمة",
    arabicLastName: "حسن",
    dateOfBirth: "1985-03-15",
    placeOfBirth: "Casablanca",
    gender: "female",
    nationality: "moroccan",
    nationalId: "AB123456",
    passportNumber: "M12345678",
    passportIssueDate: "2023-01-15",
    passportExpiryDate: "2033-01-14",
    passportIssuePlace: "Casablanca",
    email: "fatima.hassan@email.com",
    phone: "+212 6 12 34 56 78",
    whatsapp: "+212 6 12 34 56 78",
    address: "123 Rue Mohammed V",
    city: "Casablanca",
    postalCode: "20000",
    emergencyContactName: "Mohammed Hassan",
    emergencyContactRelation: t('pilgrimProfile.spouse'),
    emergencyContactPhone: "+212 6 98 76 54 32",
    photo: "https://randomuser.me/api/portraits/women/68.jpg",
    status: "active"
  };

  const mockDocuments = [
    {
      id: "DOC-001",
      type: "passport",
      url: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800",
      status: "approved",
      uploadDate: "2026-01-15T10:30:00",
      approvedDate: "2026-01-16T14:20:00"
    },
    {
      id: "DOC-002",
      type: "photo",
      url: "https://randomuser.me/api/portraits/women/68.jpg",
      status: "approved",
      uploadDate: "2026-01-15T10:35:00",
      approvedDate: "2026-01-16T14:25:00"
    },
    {
      id: "DOC-003",
      type: "medical",
      url: "https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?w=800",
      status: "pending",
      uploadDate: "2026-01-20T09:15:00"
    }
  ];

  const mockTravelInfo = {
    groupName: t('pilgrimProfile.marchOmraGroup'),
    groupType: t('pilgrimProfile.standardOmra'),
    departureDate: "2026-03-15T08:00:00",
    returnDate: "2026-03-29T22:00:00",
    makkahHotel: t('pilgrimProfile.darAlTawhidHotel'),
    madinahHotel: t('pilgrimProfile.alAqeeqHotel'),
    roomType: t('pilgrimProfile.doubleRoom'),
    outboundFlight: {
      flightNumber: "AT-501",
      airline: "Royal Air Maroc",
      departure: "Casablanca (CMN)",
      arrival: "Jeddah (JED)",
      departureTime: "2026-03-15T08:00:00",
      arrivalTime: "2026-03-15T16:30:00"
    },
    returnFlight: {
      flightNumber: "AT-502",
      airline: "Royal Air Maroc",
      departure: "Jeddah (JED)",
      arrival: "Casablanca (CMN)",
      departureTime: "2026-03-29T18:00:00",
      arrivalTime: "2026-03-29T22:30:00"
    },
    nusukSync: {
      status: "synced",
      lastSync: "2026-01-30T15:45:00"
    },
    itinerary: t('pilgrimProfile.itineraryDescription')
  };

  const mockFinancial = {
    totalAmount: 35000,
    paidAmount: 20000,
    remainingBalance: 15000,
    paymentHistory: [
      {
        id: "PAY-001",
        date: "2026-01-10T10:00:00",
        amount: 10000,
        method: "transfer",
        reference: "TRF-2026-001",
        status: "paid"
      },
      {
        id: "PAY-002",
        date: "2026-01-25T14:30:00",
        amount: 10000,
        method: "cash",
        reference: "CSH-2026-002",
        status: "paid"
      }
    ],
    invoices: [
      {
        id: "INV-001",
        number: "FAC-2026-001",
        date: "2026-01-10T10:00:00",
        amount: 10000,
        url: "#"
      },
      {
        id: "INV-002",
        number: "FAC-2026-002",
        date: "2026-01-25T14:30:00",
        amount: 10000,
        url: "#"
      }
    ]
  };

  const mockHistory = [
    {
      id: "HIST-001",
      type: "status_change",
      title: t('pilgrimProfile.fileCreated'),
      description: t('pilgrimProfile.fileCreatedDesc'),
      timestamp: "2026-01-10T09:00:00",
      performedBy: "Ahmed Benali",
      details: null
    },
    {
      id: "HIST-002",
      type: "document_upload",
      title: t('pilgrimProfile.documentsUploaded'),
      description: t('pilgrimProfile.documentsUploadedDesc'),
      timestamp: "2026-01-15T10:30:00",
      performedBy: "Fatima Hassan",
      details: {
        [t('pilgrimProfile.documentsLabel')]: t('pilgrimProfile.passportPhoto')
      }
    },
    {
      id: "HIST-003",
      type: "document_approval",
      title: t('pilgrimProfile.documentsApproved'),
      description: t('pilgrimProfile.documentsApprovedDesc'),
      timestamp: "2026-01-16T14:20:00",
      performedBy: "Ahmed Benali",
      details: null
    },
    {
      id: "HIST-004",
      type: "payment_received",
      title: t('pilgrimProfile.paymentReceived'),
      description: t('pilgrimProfile.firstPaymentReceived'),
      timestamp: "2026-01-10T10:00:00",
      performedBy: "Ahmed Benali",
      details: {
        [t('pilgrimProfile.amountLabel')]: "10,000 MAD",
        [t('pilgrimProfile.methodLabel')]: t('pilgrimProfile.transfer')
      }
    },
    {
      id: "HIST-005",
      type: "group_assignment",
      title: t('pilgrimProfile.assignedToGroup'),
      description: t('pilgrimProfile.assignedToGroupDesc'),
      timestamp: "2026-01-20T11:00:00",
      performedBy: "Ahmed Benali",
      details: {
        [t('pilgrimProfile.groupLabel')]: t('pilgrimProfile.marchOmraGroup')
      }
    }
  ];

  const tabs = [
    { id: 'personal', label: t('pilgrimProfile.personalInfo'), icon: 'User' },
    { id: 'documents', label: t('pilgrimProfile.documents'), icon: 'FileText' },
    { id: 'travel', label: t('pilgrimProfile.travelInfo'), icon: 'Plane' },
    { id: 'financial', label: t('pilgrimProfile.financial'), icon: 'DollarSign' },
    { id: 'history', label: t('pilgrimProfile.caseHistory'), icon: 'History' }
  ];

  const handleAction = (action) => {
    switch (action) {
      case 'requestDocument':
        handleRequestDocument();
        break;
      case 'processPayment':
        handleProcessPayment();
        break;
      case 'sendMessage':
        handleSendMessage();
        break;
      default:
        console.log('Action triggered:', action);
    }
  };

  const handleRequestDocument = () => {
    console.log('Requesting document');
  };

  const handleSendMessage = () => {
    console.log('Sending message');
  };

  const handleUpdatePilgrim = (updatedData) => {
    console.log('Updating pilgrim:', updatedData);
  };

  const handleDocumentUpload = (docType, file) => {
    console.log('Uploading document:', docType, file);
  };

  const handleDocumentApprove = (docId) => {
    console.log('Approving document:', docId);
  };

  const handleDocumentReject = (docId) => {
    console.log('Rejecting document:', docId);
  };

  const handleGenerateInvoice = () => {
    alert(
      t('pilgrimProfile.invoiceGenerationMessage')?.replace('{firstName}', currentLanguage === 'ar' ? mockPilgrim?.arabicFirstName : mockPilgrim?.firstName)?.replace('{lastName}', currentLanguage === 'ar' ? mockPilgrim?.arabicLastName : mockPilgrim?.lastName)?.replace('{totalAmount}', mockFinancial?.totalAmount)?.replace('{paidAmount}', mockFinancial?.paidAmount)?.replace('{remainingBalance}', mockFinancial?.remainingBalance)
    );
  };

  const handleProcessPayment = () => {
    const paymentAmount = prompt(
      t('pilgrimProfile.enterPaymentAmount')?.replace('{remainingBalance}', mockFinancial?.remainingBalance)
    );

    if (paymentAmount && !isNaN(paymentAmount) && parseFloat(paymentAmount) > 0) {
      const amount = parseFloat(paymentAmount);
      if (amount > mockFinancial?.remainingBalance) {
        alert(t('pilgrimProfile.amountExceedsBalance'));
        return;
      }

      const paymentMethod = prompt(t('pilgrimProfile.selectPaymentMethod'));

      if (paymentMethod) {
        alert(
          t('pilgrimProfile.paymentSuccessMessage')?.replace('{amount}', amount)?.replace('{method}', paymentMethod)?.replace('{newBalance}', mockFinancial?.remainingBalance - amount)
        );
      }
    } else if (paymentAmount !== null) {
      alert(t('pilgrimProfile.invalidAmount'));
    }
  };

  const handleLogout = () => {
    console.log('Logging out');
  };

  const handleLanguageChange = (language) => {
    console.log('Changing language to:', language);
  };

  const handleNotificationClick = (notification) => {
    console.log('Notification clicked:', notification);
  };

  const handleMarkAsRead = (notificationId) => {
    console.log('Marking notification as read:', notificationId);
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationSidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div
        className={`transition-smooth ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'
        }`}
      >
        <header className="sticky top-0 z-30 bg-card border-b border-border shadow-warm">
          <div className="flex items-center justify-between pl-16 pr-4 md:px-6 lg:px-8 py-4">
            <div className="flex-1" />
            <div className="flex items-center gap-3 md:gap-4">
              <NotificationCenter
                notifications={mockNotifications}
                onNotificationClick={handleNotificationClick}
                onMarkAsRead={handleMarkAsRead}
                currentLanguage={currentLanguage}
              />
              <UserProfileDropdown
                user={mockUser}
                onLogout={handleLogout}
                onLanguageChange={handleLanguageChange}
                currentLanguage={currentLanguage}
              />
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <BreadcrumbNavigation currentLanguage={currentLanguage} />

          <div className="space-y-6 md:space-y-8">
            <ProfileHeader
              pilgrim={mockPilgrim}
              onAction={handleAction}
              currentLanguage={currentLanguage}
            />

            <div className="bg-card rounded-lg border border-border shadow-warm overflow-hidden">
              <div className="border-b border-border overflow-x-auto">
                <div className="flex min-w-max">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 text-sm md:text-base font-medium transition-smooth whitespace-nowrap ${
                        activeTab === tab?.id
                          ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon name={tab?.icon} size={18} />
                      <span>{tab?.label?.[currentLanguage]}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-4 md:p-6 lg:p-8">
                {activeTab === 'personal' && (
                  <PersonalInfoTab
                    pilgrim={mockPilgrim}
                    onUpdate={handleUpdatePilgrim}
                    currentLanguage={currentLanguage}
                  />
                )}
                {activeTab === 'documents' && (
                  <DocumentsTab
                    documents={mockDocuments}
                    onUpload={handleDocumentUpload}
                    onApprove={handleDocumentApprove}
                    onReject={handleDocumentReject}
                    currentLanguage={currentLanguage}
                  />
                )}
                {activeTab === 'travel' && (
                  <TravelInfoTab
                    travelInfo={mockTravelInfo}
                    currentLanguage={currentLanguage}
                  />
                )}
                {activeTab === 'financial' && (
                  <FinancialTab
                    financial={mockFinancial}
                    onGenerateInvoice={handleGenerateInvoice}
                    onProcessPayment={handleProcessPayment}
                    currentLanguage={currentLanguage}
                  />
                )}
                {activeTab === 'history' && (
                  <CaseHistoryTab
                    history={mockHistory}
                    currentLanguage={currentLanguage}
                  />
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PilgrimProfile;